import { getDevicesList } from './action/DeviceActions';
import { getEquipmentsList } from './action/EquipmentActions';
import { getUsers } from './action/ClientActions';
import { getAllCompanies, getCompanyById, getCompanyDrivers, getCompanyProfiles } from './action/CompaniesActions';
import { getFlaggedEvents } from './action/AlertActions';

import { clearAll } from './apiCache';
import { userMenus } from '../../app/layout/side-menu/MenuItems';
import { requestPriorityManager } from './requestPriority';

let lastPrefetchedCompanyId = null;

const hasMenuAccess = (userRole, ...linkTos) => {
	const roleMenu = userMenus[userRole];
	if (!roleMenu) return false;
	return [...(roleMenu.menu || []), ...(roleMenu.collapsible || [])]
		.some(({ linkTo }) => linkTos.includes(linkTo));
};

let pendingScheduleHandle = null;
let pendingScheduleIsIdleCallback = false;

const cancelScheduledPrefetch = () => {
	if (pendingScheduleHandle === null) return;
	if (pendingScheduleIsIdleCallback && typeof window.cancelIdleCallback === 'function') {
		window.cancelIdleCallback(pendingScheduleHandle);
	} else {
		clearTimeout(pendingScheduleHandle);
	}
	pendingScheduleHandle = null;
};

const scheduleAtLowestPriority = (callback) => {
	cancelScheduledPrefetch();

	const run = () => {
		pendingScheduleHandle = null;
		callback();
	};

	if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
		pendingScheduleHandle = window.requestIdleCallback(run, { timeout: 300 });
		pendingScheduleIsIdleCallback = true;
	} else {
		pendingScheduleHandle = setTimeout(run, 0);
		pendingScheduleIsIdleCallback = false;
	}
};

export const clearPrefetchCache = () => {
	cancelScheduledPrefetch();
	clearAll();
	requestPriorityManager.abortPrefetch();
	lastPrefetchedCompanyId = null;
};

export const prefetchData = (userRole, company) => {
	if (!userRole || !company?.company_id || lastPrefetchedCompanyId === company.company_id) return;

	if (lastPrefetchedCompanyId !== null) {
		clearPrefetchCache();
	}

	lastPrefetchedCompanyId = company.company_id;
	scheduleAtLowestPriority(() => runPrefetch(userRole, company));
};

const buildPrefetchRequests = (userRole, company) => {
	const requests = [];

	// Companies page, Firmware rollout blacklist modal
	if (hasMenuAccess(userRole, '/company', '/fleet-admin/firmware')) {
		requests.push(() => getAllCompanies(userRole, 'customer,wcs'));
	}

	// Partners page
	if (hasMenuAccess(userRole, '/partner')) {
		requests.push(() => getAllCompanies(userRole, 'partner'));
	}

	// Users page + user pickers on Events/Reports/Groups/Company pages
	if (hasMenuAccess(userRole, '/manageusers', '/event', '/reports', '/managegroups', '/company')) {
		requests.push(() => getUsers(userRole, company));
	}

	// Assets page
	requests.push(() => getCompanyProfiles({ user_role: userRole, company_name: company.name, partner_name: company.partner }));

	// Drivers page + driver info on Home/Events/Groups pages
	if (hasMenuAccess(userRole, '/', '/event', '/managedrivers', '/managegroups')) {
		requests.push(() => getCompanyDrivers({ companyId: company.company_id }));
		requests.push(() => getCompanyDrivers({ companyId: company.company_id, include_data: 'photos' }));
	}

	// Devices page
	requests.push(() => getDevicesList(company.company_id, 'inactive_devices,latest_hdderror,data_usage,ignition,sd_status,channel_status,driver,live_stream,no_sd_card,camera_health,sd_wear,migration'));
	requests.push(() => getCompanyById(userRole, company.company_id, 'data_usage'));

	// Events page: Flagged
	if (hasMenuAccess(userRole, '/event')) {
		requests.push(() => getFlaggedEvents({ company_id: company.company_id }));
	}

	return requests;
};

const runPrefetch = (userRole, company) => {
	const requests = buildPrefetchRequests(userRole, company);
	if (requests.length > 0) {
		requestPriorityManager.enqueuePrefetchBatch(requests)
			.catch(error => console.debug('Prefetch batch error:', error?.message));
	}
};
